// ---------------------------------------------------------------------------
// Remount bus — bring a dead view back without reloading the page
//
// A full reload throws away in-memory state, re-downloads the shell and costs
// seconds. Almost every render crash only needs the subtree torn down and
// rebuilt. Repair actions publish here; every CrashShield listens and rebuilds
// its children under a fresh key.
// ---------------------------------------------------------------------------

type Listener = (reason: string) => void;

const listeners = new Set<Listener>();
let lastAt = 0;

/** Ask every mounted crash shield to rebuild its subtree. */
let deferred: string | null = null;

function typingNow(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable;
}

function playbackOnScreen(): boolean {
  if (typeof document === "undefined") return false;
  // Rebuilding while a text box is focused throws the phone keyboard away.
  if (typingNow()) return true;
  for (const v of Array.from(document.querySelectorAll("video"))) if (!v.paused && !v.ended) return true;
  return !!document.querySelector('iframe[src*="youtube"],iframe[src*="youtube-nocookie"],iframe[src*="vimeo"],iframe[src*="archive.org"],iframe[src*="dailymotion"]');
}

export function requestRemount(reason = "repair"): void {
  // Rebuilding the screen tears the film or YouTube frame out mid-scene —
  // that was the "cuts out halfway through". Hold the repair until playback
  // stops or the page is hidden, then carry it out.
  if (playbackOnScreen() && (reason !== "chunk-retry" || typingNow())) {
    if (!deferred) {
      deferred = reason;
      const tick = window.setInterval(() => {
        if (playbackOnScreen() && document.visibilityState === "visible") return;
        window.clearInterval(tick);
        const r = deferred ?? reason; deferred = null;
        requestRemount(r);
      }, 5_000);
    }
    return;
  }
  lastAt = Date.now();
  for (const fn of listeners) {
    try { fn(reason); } catch { /* a listener must never break a repair */ }
  }
}

export function subscribeRemount(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function lastRemountAt(): number { return lastAt; }
